import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  MoreVertical,
  Eye,
  EyeOff,
  ClipboardList,
  ClipboardCheck,
  ArrowDownToLine,
  ArrowUpFromLine,
  BarChart2,
} from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui";

interface InventoryRowActionsProps {
  ndc: string;
  itemName: string;
  inventoryId?: string;
  isHidden?: boolean;
  /** When true, only view-only actions (View Details, Activity) are shown. */
  readOnly?: boolean;
  onViewDetails?: () => void;
  onEdit?: () => void;
  onViewActivity?: () => void;
  onToggleHidden?: () => void;
  onDelete?: () => void;
  onReceive?: (ndc: string, itemName: string) => void;
  onDispense?: (ndc: string, itemName: string) => void;
  onAddToReminder?: (inventoryId: string, itemName: string) => void;
  onAudit?: (inventoryId: string) => void;
}

export function InventoryRowActions({
  ndc,
  itemName,
  inventoryId,
  isHidden = false,
  readOnly = false,
  onViewDetails,
  onEdit,
  onViewActivity,
  onToggleHidden,
  onDelete,
  onReceive,
  onDispense,
  onAddToReminder,
  onAudit,
}: InventoryRowActionsProps) {
  const navigate = useNavigate();

  const handleReceive = () => {
    if (onReceive) {
      onReceive(ndc, itemName);
    } else {
      navigate({
        to: `/receipts?action=create&ndc=${encodeURIComponent(ndc)}&itemName=${encodeURIComponent(itemName)}`,
      });
    }
  };

  const handleDispense = () => {
    if (onDispense) {
      onDispense(ndc, itemName);
    } else {
      navigate({
        to: `/dispenses?action=create&ndc=${encodeURIComponent(ndc)}&itemName=${encodeURIComponent(itemName)}`,
      });
    }
  };

  const handleView = () => {
    if (onViewDetails) {
      onViewDetails();
    }
  };

  const handleActivity = () => {
    if (onViewActivity) {
      onViewActivity();
    } else {
      navigate({ to: `/activity?search=${encodeURIComponent(ndc)}` });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 data-[state=open]:bg-muted"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">More actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px]">
        {!readOnly && (
          <>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleReceive();
              }}
            >
              <ArrowDownToLine className="mr-2 h-4 w-4" />
              Receive
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleDispense();
              }}
            >
              <ArrowUpFromLine className="mr-2 h-4 w-4" />
              Dispense
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleActivity();
          }}
        >
          <BarChart2 className="mr-2 h-4 w-4" />
          Activity
        </DropdownMenuItem>
        {!readOnly && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              if (onAudit && inventoryId) {
                onAudit(inventoryId);
              }
            }}
          >
            <ClipboardCheck className="mr-2 h-4 w-4" />
            Audit
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleView();
          }}
        >
          View Details
        </DropdownMenuItem>
        {!readOnly && (
          <>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.();
              }}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (onAddToReminder && inventoryId) {
                  onAddToReminder(inventoryId, itemName);
                } else if (inventoryId) {
                  navigate({
                    to: `/reminders?action=create&referenceType=inventory&referenceId=${encodeURIComponent(inventoryId)}&referenceName=${encodeURIComponent(itemName)}`,
                  });
                }
              }}
            >
              <ClipboardList className="mr-2 h-4 w-4" />
              Add to Reminders
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onToggleHidden?.();
              }}
            >
              {isHidden ? (
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  Unhide Item
                </>
              ) : (
                <>
                  <EyeOff className="mr-2 h-4 w-4" />
                  Hide Item
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
              className="text-danger focus:text-danger-foreground focus:bg-danger"
            >
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
